import React from 'react';
import { Box, Button, ButtonBase, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PhoneLegendItem } from './phoneChartOptions';

export interface LegendChipsProps {
  /** One chip per series, in the chart's colours. */
  items: PhoneLegendItem[];
  /** Ids of the hidden series. */
  hidden: ReadonlySet<string>;
  /** A chip was tapped: show or hide that series. */
  onToggle: (id: string) => void;
  /** 'All' was tapped: show every series. */
  onShowAll: () => void;
}

/** More series than this get an 'All' chip that shows them all again. */
const ALL_CHIP_FROM = 4;

/** Each chip sits in a 44px row and its tap area reaches the row's height,
 *  although the chip itself is 36px. */
const ChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
    {children}
  </Box>
);

const chipSx = {
  position: 'relative',
  minHeight: 36,
  height: 'auto',
  maxWidth: '100%',
  fontWeight: 500,
  '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 },
  // The tap area covers the whole 44px row.
  '&::after': { content: '""', position: 'absolute', top: -4, bottom: -4, left: 0, right: 0 },
} as const;

/**
 * The phone charts' legend: wrapping chips below the chart, a colour swatch
 * and the series name each. A tap hides or shows that series (the chip
 * turns outlined with a hollow swatch); 'All' shows every series again.
 */
export const LegendChips: React.FC<LegendChipsProps> = ({ items, hidden, onToggle, onShowAll }) => {
  const { t } = useTranslation();
  const allShown = items.every((item) => !hidden.has(item.id));
  return (
    <Box
      role="group"
      aria-label={t('mobile.charts.legend', { defaultValue: 'Series on the chart' })}
      sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.75, mt: 0.5, minWidth: 0 }}
    >
      {items.length >= ALL_CHIP_FROM && (
        <ChipRow>
          <Chip
            clickable
            label={t('mobile.charts.legendAll', { defaultValue: 'All' })}
            onClick={onShowAll}
            aria-pressed={allShown}
            color={allShown ? 'primary' : 'default'}
            variant={allShown ? 'filled' : 'outlined'}
            sx={chipSx}
          />
        </ChipRow>
      )}
      {items.map((item) => {
        const shown = !hidden.has(item.id);
        return (
          <ChipRow key={item.id}>
            <Chip
              clickable
              onClick={() => onToggle(item.id)}
              aria-pressed={shown}
              variant={shown ? 'filled' : 'outlined'}
              icon={
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 12,
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: `2px solid ${item.color}`,
                    bgcolor: shown ? item.color : 'transparent',
                    ml: '10px !important',
                  }}
                />
              }
              label={item.label}
              sx={{ ...chipSx, color: shown ? 'text.primary' : 'text.secondary' }}
            />
          </ChipRow>
        );
      })}
    </Box>
  );
};

/**
 * The Charts tab's legend: one row per series under the chart, a colour
 * swatch, the name and its newest value. A tap hides or shows that series
 * (the row greys, the swatch goes hollow); 'Show all' shows every series
 * again.
 */
export const LegendList: React.FC<LegendChipsProps> = ({ items, hidden, onToggle, onShowAll }) => {
  const { t } = useTranslation();
  const allShown = items.every((item) => !hidden.has(item.id));
  return (
    <Box
      role="group"
      aria-label={t('mobile.charts.legend', { defaultValue: 'Series on the chart' })}
      data-testid="legend-list"
      sx={{ mt: 1, minWidth: 0 }}
    >
      {items.length >= ALL_CHIP_FROM && !allShown && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onShowAll} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}>
            {t('mobile.charts.legendShowAll', { defaultValue: 'Show all' })}
          </Button>
        </Box>
      )}
      {items.map((item, i) => {
        const shown = !hidden.has(item.id);
        return (
          <ButtonBase
            key={item.id}
            onClick={() => onToggle(item.id)}
            aria-pressed={shown}
            aria-label={item.label}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: 44,
              py: 0.75,
              textAlign: 'left',
              borderTop: i > 0 ? '1px solid' : 'none',
              borderColor: 'divider',
              opacity: shown ? 1 : 0.55,
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                flexShrink: 0,
                borderRadius: '3px',
                border: `2px solid ${item.color}`,
                bgcolor: shown ? item.color : 'transparent',
              }}
            />
            <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', overflowWrap: 'anywhere' }}>
              {item.label}
            </Typography>
            {item.value && (
              <Typography component="span" aria-hidden sx={{ fontSize: '0.875rem', fontWeight: 650, whiteSpace: 'nowrap' }}>
                {item.value}
              </Typography>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default LegendChips;
